/** Customer support tickets, /app/support (PREC-3937; own file — en.ts is at the size ratchet). */
export const enSupport = {
	title: "Support",
	newTicket: "New ticket",
	myTickets: "My tickets",
	reportProblem: "Report a problem",
	truncated: "Showing the 100 most recently updated tickets.",
	tabs: {
		ALL: "All",
		ACTIVE: "Active",
		NEEDS_REPLY: "Needs your reply",
		RESOLVED: "Resolved",
		CLOSED: "Closed",
	},
	status: {
		OPEN: "Open",
		WAITING_ON_US: "With Signalist",
		WAITING_ON_CUSTOMER: "Needs your reply",
		RESOLVED: "Resolved",
		CLOSED: "Closed",
	},
	category: {
		BUG: "Bug",
		QUESTION: "Question",
		FEATURE_REQUEST: "Feature request",
		BILLING: "Billing",
		DATA_QUALITY: "Data quality",
		TASK: "Task",
	},
	columns: {
		ticket: "Ticket",
		subject: "Subject",
		category: "Category",
		status: "Status",
		attachments: "Files",
		lastActivity: "Last activity",
	},
	activity: {
		FILED: "Filed",
		CUSTOMER_REPLIED: "You replied",
		SUPPORT_REPLIED: "Signalist replied",
		RESOLVED: "Marked resolved",
		CLOSED: "Closed",
	},
	linkedEntity: {
		LIST: "List #{{id}}",
		CAMPAIGN: "Campaign #{{id}}",
		LEAD: "Lead #{{id}}",
		CONVERSATION: "Conversation #{{id}}",
	},
	filters: {
		searchPlaceholder: "Search tickets",
		categoryAll: "All categories",
		scopeCompany: "All of {{company}}",
		scopeMine: "Only mine",
		companyAdminHint: "You are a company admin, so you also see your colleagues' tickets.",
	},
	empty: {
		title: "No tickets yet",
		body: "When something goes wrong or you have a question, file a ticket and the Signalist team answers here.",
		filtered: "No ticket matches these filters.",
	},
	modal: {
		title: "New support ticket",
		intro:
			"Tell us what is going wrong or what you need. The Signalist team answers here and you get an e-mail when they reply.",
		categoryLabel: "What is it about?",
		subjectLabel: "Subject",
		subjectPlaceholder: "Short summary of the problem",
		messageLabel: "Describe the problem",
		messagePlaceholder: "What did you expect to happen, and what happened instead?",
		messageHelp: "Include what you expected to happen and what happened instead.",
		relatedLabel: "Related to",
		optional: "(optional)",
		attachmentsLabel: "Attachments",
		dropzone: "Upload a file or drag it here",
		dropzoneHint: "Screenshots, CSV, PDF · up to {{maxFiles}} files, {{maxMb}} MB each",
		filedAs: "Filed as {{name}} · {{company}}",
		submit: "Send ticket",
		cancel: "Cancel",
		remove: "Remove {{name}}",
		created: "Ticket #{{id}} created.",
		createdWithFailedUpload: "Ticket #{{id}} created, but {{name}} could not be attached.",
		campaignSubject: "Campaign “{{name}}”",
		campaignBody: "Campaign: {{name}} ({{campaignId}})\n\n",
		errors: {
			subjectRequired: "Give the ticket a subject.",
			messageRequired: "Describe the problem so support can act on it.",
			tooLarge: "{{name}} is larger than {{maxMb}} MB.",
			tooMany: "You can attach at most {{maxFiles}} files.",
			wrongType: "{{name}} is not a supported file type.",
			createFailed: "The ticket could not be created. Please try again.",
		},
	},
	reportProblemSubject: {
		lead: "Lead “{{name}}”",
		conversation: "Conversation with “{{name}}”",
	},
	detail: {
		breadcrumb: "Support",
		ticketNumber: "#{{id}}",
		openedBy: "Opened by {{name}} on {{date}}",
		related: "Related: {{entity}}",
		hint: {
			FILED: "Filed {{ago}}",
			CUSTOMER_REPLIED: "You replied {{ago}}",
			SUPPORT_REPLIED: "Signalist replied {{ago}}",
			RESOLVED: "Resolved {{ago}}",
			CLOSED: "Closed {{ago}}",
		},
		thread: {
			staffRole: "Signalist Support",
			received: "Ticket received · you will get an e-mail when Signalist replies",
			statusChanged: "Status changed",
			transcript: "Chat with the assistant before this ticket",
			transcriptCount: "· {{count}} messages",
			assistant: "Assistant",
		},
		attachments: {
			open: "Open {{name}}",
			failed: "{{name}} could not be opened. Please try again.",
			none: "No files attached",
		},
		composer: {
			placeholder: "Reply to Signalist…",
			attach: "Attach",
			hint: "Up to {{maxFiles}} files, {{maxMb}} MB each",
			send: "Send reply",
			sent: "Reply sent.",
			sentWithFailedUpload: "Reply sent, but {{name}} could not be attached.",
			failed: "Your reply could not be sent. Please try again.",
		},
		banner: {
			title: "{{name}} marked this ticket as resolved",
			titleAnonymous: "Signalist marked this ticket as resolved",
			body: "Does this solve your problem? If we don't hear back, the ticket closes automatically in {{days}} days.",
			confirm: "Yes, close it",
			reply: "Not yet, reply",
			closed: "Ticket closed. Thanks for confirming.",
			confirmFailed: "The ticket could not be closed. Please reload and try again.",
		},
		rail: {
			status: "Status",
			details: "Details",
			attachments: "Attachments",
			notifications: "Notifications",
			notificationsHint:
				"You get an in-app notification and an e-mail whenever Signalist replies or the status changes.",
			change: "Change",
			ticket: "Ticket",
			category: "Category",
			opened: "Opened",
			openedBy: "Opened by",
			relatedTo: "Related to",
			source: "Source",
			explanation: {
				OPEN: "Signalist has received your ticket and will answer here.",
				WAITING_ON_US: "Signalist is looking into it.",
				WAITING_ON_CUSTOMER: "Signalist answered and is waiting for you.",
				RESOLVED:
					"Marked resolved by {{name}} on {{date}}. Closes automatically on {{closeDate}} unless you reply.",
				CLOSED: "Closed on {{date}}. Reply to reopen it.",
			},
		},
		source: {
			CHAT_BOT: "Help assistant",
			FORM: "Support form",
			STAFF: "Filed by Signalist",
			EMAIL: "Email",
			SYSTEM: "Opened automatically by Signalist",
		},
	},
	/** The staff queue, /app/tickets (PREC-3939). Speaks from Signalist's side: "waiting on us". */
	staff: {
		title: "Tickets",
		newTicketForCustomer: "New ticket for a customer",
		strip: {
			waitingOnUs: "Waiting on us",
			oldest: "oldest {{age}}",
			openUntriaged: "Open, untriaged",
			unassigned: "Unassigned",
			waitingOnCustomer: "Waiting on customer",
			resolvedAwaitingClose: "Resolved, awaiting close",
			autoCloseIn: "auto-close in {{window}}",
			medianFirstReply: "Median first reply",
			last30Days: "last 30 d",
			noData: "—",
		},
		tabs: {
			NEEDS_ACTION: "Needs action",
			MINE: "Mine",
			UNASSIGNED: "Unassigned",
			ALL_OPEN: "All open",
			RESOLVED: "Resolved",
			CLOSED: "Closed",
		},
		status: {
			OPEN: "Open",
			WAITING_ON_US: "Waiting on us",
			WAITING_ON_CUSTOMER: "Waiting on customer",
			RESOLVED: "Resolved",
			CLOSED: "Closed",
		},
		priority: { URGENT: "Urgent", HIGH: "High", NORMAL: "Normal", LOW: "Low" },
		units: { MIN: "{{value}} min", HOUR: "{{value}} h", DAY: "{{value}} d" },
		columns: {
			ticket: "Ticket",
			priority: "Priority",
			subject: "Subject · Company · Filed by",
			category: "Category",
			status: "Status",
			assignee: "Assignee",
			lastActivity: "Last activity",
			age: "Age",
		},
		filters: {
			searchPlaceholder: "Search tickets, #id, company",
			statusLabel: "Status",
			assigneeAnyone: "Assignee: Anyone",
			assigneeUnassigned: "Unassigned",
			assigneeMine: "Mine",
			categoryAll: "Category: All",
			priorityAll: "Priority: All",
			companyAll: "Company: All",
			clearCompany: "Clear company",
		},
		assignee: { unassigned: "Unassigned" },
		activity: { CUSTOMER: "Customer", FILED: "Filed", STAFF: "Signalist", CLOSED: "Closed" },
		uiOff: "UI off",
		uiOffHint:
			"This company has no Support page yet: the customer gets e-mails but cannot reply in-app.",
		empty: { none: "No tickets in this view.", filtered: "No ticket matches these filters." },
		modal: {
			title: "New ticket for a customer",
			intro:
				"File a ticket on the customer's behalf. It lands in their Support page and they get an e-mail when you reply.",
			companyLabel: "Company",
			companyPlaceholder: "Search a company",
			customerLabel: "Customer",
			customerPlaceholder: "Pick a company first",
			customerEmpty: "This company has no users.",
			pickerRequired: "Pick the company and the customer this ticket is for.",
			filedFor: "Filed for {{name}} · {{company}}",
			created: "Ticket #{{id}} created for {{name}}.",
			createdWithFailedUpload:
				"Ticket #{{id}} created for {{name}}, but {{file}} could not be attached.",
		},
		/** The staff detail, /app/tickets/:id (PREC-3940). */
		detail: {
			breadcrumb: "Tickets",
			previous: "Previous",
			next: "Next",
			loginAs: "Log in as support user",
			loginAsHint: "Logs in as {{email}}",
			noSupportUser: "No Signalist support user in this company",
			loginFailed: "Could not log in as the support user. Please try again.",
			openedVia: "Opened {{date}} via {{source}}",
			sourceShort: {
				CHAT_BOT: "help assistant",
				FORM: "form",
				STAFF: "Signalist",
				EMAIL: "email",
				SYSTEM: "an automatic report",
			},
			/** Badge on the detail header (PREC-3978) so staff see at a glance that a reply mails out. */
			source: { email: "Email" },
			related: "Related: {{entity}}",
			page: "Page:",
			thread: {
				received: "Ticket received",
				statusChanged: "Status changed",
				internalNote: "Internal note · not visible to the customer",
			},
			composer: {
				placeholderPublic: "Reply to {{name}}…",
				placeholderInternal: "Note for the team…",
				internalToggle: "Internal note",
				hintPublic: "Customer sees this · e-mails {{name}}",
				hintInternal: "Staff only · customer not notified",
				thenSet: "Then set",
				sendReply: "Send reply",
				addNote: "Add note",
				sent: "Reply sent.",
				noteAdded: "Note added.",
				sentWithFailedUpload: "Sent, but {{name}} could not be attached.",
				failed: "Could not send. Please try again.",
			},
			rail: {
				triage: "Triage",
				status: "Status",
				assignee: "Assignee",
				unassigned: "Unassigned",
				priority: "Priority",
				category: "Category",
				markResolved: "Mark as resolved",
				updated: "Ticket updated.",
				assigneeNotStaff: "That user is not Signalist staff and cannot be assigned.",
				updateFailed: "The change could not be saved. Please try again.",
				customer: "Customer",
				role: { ADMIN: "Company admin", USER: "User" },
				plan: "{{plan}} plan",
				noPlan: "No plan",
				users: "{{count}} users",
				customerSince: "Customer since {{date}}",
				openTickets: "{{count}} open tickets",
				loginAs: "Log in as",
				supportDashboard: "Support Dashboard",
				context: "Context",
				relatedTo: "Related to",
				page: "Page",
				source: "Source",
				opened: "Opened",
				firstReply: "First reply",
				waitingSince: "Waiting since",
				none: "—",
				attachments: "Attachments · {{count}}",
				noAttachments: "No files attached",
				otherTickets: "Other tickets from {{company}}",
			},
		},
	},
};
